import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BusinessProvider } from './context/BusinessContext';
import { ModalProvider } from './context/ModalContext';
import DevEnvBanner from './components/DevEnvBanner';
import LoginScreen from './screens/LoginScreen';
import SignupScreen from './screens/SignupScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import DashboardScreen from './screens/DashboardScreen';
import FacilitySetupScreen from './screens/FacilitySetupScreen';
import CourtsPricingScreen from './screens/CourtsPricingScreen';
import SuperAdminScreen from './screens/SuperAdminScreen';
import SuperAdminManualBookingRequestsScreen from './screens/SuperAdminManualBookingRequestsScreen';
import SuperAdminQuoteRequestsScreen from './screens/SuperAdminQuoteRequestsScreen';
import SuperAdminSettlementLedgerScreen from './screens/SuperAdminSettlementLedgerScreen';
import AudienceBuilderScreen from './screens/AudienceBuilderScreen';
import RevenueOpportunitiesScreen from './screens/RevenueOpportunitiesScreen';
import { REVENUE_PATH, REVENUE_AUDIENCES_PATH } from './utils/revenueLinks';
import PromoCodesScreen from './screens/PromoCodesScreen';
import PromoAnalyticsScreen from './screens/PromoAnalyticsScreen';
import IntegrationsScreen from './screens/IntegrationsScreen';
import TeamScreen from './screens/TeamScreen';
import BookingsScreen from './screens/BookingsScreen';
import PaymentsScreen from './screens/PaymentsScreen';
import GoLiveScreen from './screens/GoLiveScreen';
import './styles/index.css';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading" style={{ minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// This is deliberately separate from ProtectedRoute. Hiding the sidebar item is
// only a convenience; the route itself must not render for venue-level admins.
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isSuperAdmin, loading } = useAuth();

  if (loading) {
    return <div className="loading" style={{ minHeight: '100vh' }}><div className="spinner" /></div>;
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isSuperAdmin) return <Navigate to="/bookings" replace />;
  return <>{children}</>;
}

// Public Route wrapper (redirects to dashboard if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading" style={{ minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/bookings" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginScreen />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupScreen />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordScreen />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings"
        element={
          <ProtectedRoute>
            <BookingsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/facility-setup"
        element={
          <ProtectedRoute>
            <FacilitySetupScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courts-pricing"
        element={
          <ProtectedRoute>
            <CourtsPricingScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/superadmin"
        element={
          <SuperAdminRoute>
            <SuperAdminScreen />
          </SuperAdminRoute>
        }
      />
      {/* Revenue is one page; the Audiences workspace opens from it. */}
      <Route
        path={REVENUE_PATH}
        element={<ProtectedRoute><RevenueOpportunitiesScreen /></ProtectedRoute>}
      />
      <Route
        path={REVENUE_AUDIENCES_PATH}
        element={<ProtectedRoute><AudienceBuilderScreen /></ProtectedRoute>}
      />
      {/* Old addresses, kept for bookmarks and emailed links. */}
      <Route path="/audiences" element={<Navigate to={REVENUE_AUDIENCES_PATH} replace />} />
      <Route path="/revenue-opportunities" element={<Navigate to={REVENUE_PATH} replace />} />
      <Route
        path="/superadmin/manual-booking-requests"
        element={
          <SuperAdminRoute>
            <SuperAdminManualBookingRequestsScreen />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/superadmin/quote-requests"
        element={
          <SuperAdminRoute>
            <SuperAdminQuoteRequestsScreen />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/superadmin/settlement-ledger"
        element={
          <SuperAdminRoute>
            <SuperAdminSettlementLedgerScreen />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/superadmin/promos"
        element={
          <SuperAdminRoute>
            <PromoCodesScreen />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/superadmin/promos/analytics"
        element={
          <SuperAdminRoute>
            <PromoAnalyticsScreen />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/superadmin/integrations"
        element={
          <SuperAdminRoute>
            <IntegrationsScreen />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <SuperAdminRoute>
            <Navigate to="/superadmin/integrations" replace />
          </SuperAdminRoute>
        }
      />
<Route
        path="/team"
        element={
          <ProtectedRoute>
            <TeamScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <PaymentsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/go-live"
        element={
          <ProtectedRoute>
            <GoLiveScreen />
          </ProtectedRoute>
        }
      />
      {/* Redirect old routes to appropriate pages */}
      <Route path="/business-profile" element={<Navigate to="/facility-setup" replace />} />
      <Route path="/courts" element={<Navigate to="/courts-pricing" replace />} />
      <Route path="/pricing" element={<Navigate to="/courts-pricing" replace />} />
      <Route path="/policies" element={<Navigate to="/facility-setup" replace />} />
      <Route path="/photos" element={<Navigate to="/facility-setup" replace />} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/bookings" replace />} />
      <Route path="*" element={<Navigate to="/bookings" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BusinessProvider>
          <ModalProvider>
            <DevEnvBanner />
            <AppRoutes />
          </ModalProvider>
        </BusinessProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
